import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Crown, Check, Sparkles, Zap, ShieldCheck, Infinity as InfinityIcon, Copy } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProductBoxArt from '@/components/ProductBoxArt';
import ReviewsMarquee from '@/components/ReviewsMarquee';
import { api } from '@/lib/api';

const DISCORD_INVITE = 'https://discord.gg/luckyscripts';

function ProductGrid({ items, testid, sale }) {
  const priceOf = (p) => {
    const base = p.price_aud;
    const salePrice = computeSalePrice(base, sale, 'product');
    return { base, salePrice };
  };
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-120px' }}
      variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      data-testid={testid}
    >
      {items.map((p) => {
        const { base, salePrice } = priceOf(p);
        return (
          <motion.div
            key={p.id}
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
            }}
          >
            <Link
              to={`/product/${p.id}`}
              data-testid={`product-card-${p.id}`}
              className="block group relative rounded-2xl p-5 transition-all duration-300"
              style={{
                background: 'linear-gradient(180deg, rgba(14,22,42,0.75), rgba(8,12,24,0.9))',
                border: '1px solid rgba(140,170,220,0.1)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = (p.accent || '#4DA3FF') + '66'; e.currentTarget.style.boxShadow = `0 20px 60px -20px ${p.accent || '#4DA3FF'}55`; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(140,170,220,0.1)'; e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.03)'; }}
            >
              {salePrice && (
                <div className="absolute top-3 right-3 z-10 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono font-semibold bg-emerald-400/15 border border-emerald-400/40 text-emerald-300">
                  −{Math.round((1 - salePrice / base) * 100)}%
                </div>
              )}
              <div className="transition-transform duration-300 group-hover:-translate-y-1">
                <ProductBoxArt product={p} />
              </div>
              <div className="mt-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-white font-display font-semibold text-xl leading-tight truncate">{p.name}</div>
                    <div className="text-white/45 text-[11px] uppercase tracking-wider mt-1 font-mono">{p.tagline}</div>
                  </div>
                  <div className="text-right shrink-0">
                    {salePrice ? (
                      <div className="flex items-baseline gap-1.5 justify-end">
                        <span className="font-display text-white font-bold text-lg">${salePrice}</span>
                        <span className="text-white/35 line-through text-xs">${base}</span>
                      </div>
                    ) : (
                      <div className="font-display text-white font-bold text-lg">${base} <span className="text-white/40 text-xs">USD</span></div>
                    )}
                    {p.coming_soon && (
                      <div className="text-[10px] uppercase tracking-widest text-brand mt-1">Coming Soon</div>
                    )}
                  </div>
                </div>
                {(p.features && p.features.length > 0) ? (
                  <ul className="mt-4 grid grid-cols-2 gap-y-1.5 gap-x-3">
                    {p.features.slice(0, 6).map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-white/70">
                        <span
                          className="inline-flex items-center justify-center h-4 w-4 rounded-full shrink-0"
                          style={{ background: `${p.accent || '#4DA3FF'}22`, color: p.accent || '#4DA3FF' }}
                        >
                          <Check className="h-2.5 w-2.5" />
                        </span>
                        <span className="truncate">{f}</span>
                      </li>
                    ))}
                  </ul>
              ) : (p.short_description || p.description) && (
                <p className="mt-3 text-sm text-white/60 leading-relaxed line-clamp-2">
                  {p.short_description || p.description}
                </p>
              )}
            </div>
            <div
              className="pointer-events-none absolute -inset-2 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ boxShadow: '0 30px 60px -25px rgb(var(--brand-rgb)/0.5)' }}
            />
          </Link>
        </motion.div>
        );
      })}
    </motion.div>
  );
}

function EmptyCatalogState({ onVip }) {
  return (
    <div
      data-testid="products-empty-state"
      className="glass-strong rounded-3xl p-16 text-center relative overflow-hidden"
    >
      <div
        className="orb"
        style={{ top: '-30%', left: '50%', transform: 'translateX(-50%)', width: 400, height: 400,
                 background: 'radial-gradient(circle, rgb(var(--brand-rgb)/0.35), transparent 70%)' }}
      />
      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60 font-mono uppercase tracking-widest mb-6">
          <Sparkles className="h-3 w-3 text-brand" />
          New drops incoming
        </div>
        <h3 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white">
          Fresh catalog<br />
          <span className="text-brand">loading soon</span>
        </h3>
        <p className="mt-6 text-white/60 max-w-xl mx-auto leading-relaxed">
          Our next wave of premium scripts is being finalised. Grab Lucky VIP now to unlock every script the moment it drops — no re-purchase required.
        </p>
        <button
          data-testid="empty-state-vip-btn"
          onClick={onVip}
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-[#0a0a0a] font-semibold hover:shadow-[0_0_36px_rgb(var(--brand-rgb)/0.65)] transition-shadow"
        >
          <Crown className="h-4 w-4" /> Get Lucky VIP
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function DiscordCopyButton() {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(DISCORD_INVITE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      const t = document.createElement('textarea');
      t.value = DISCORD_INVITE;
      document.body.appendChild(t); t.select();
      document.execCommand('copy'); t.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };
  return (
    <button
      type="button"
      data-testid="discord-copy-btn"
      onClick={copy}
      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white/80 text-sm hover:bg-white/10 transition-colors"
    >
      {copied ? <><Check className="h-4 w-4 text-[#7B85F5]" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy invite link</>}
    </button>
  );
}

const VIP_TIERS = [
  {
    id: 'premium',
    name: 'Premium VIP',
    price: 60,
    image: '/vip-hero.png',
    accent: 'rgb(var(--brand-rgb))',
    accentSoft: 'rgb(var(--brand-rgb)/0.4)',
    tagline: 'All access · Private leaks, VIP tools & every script we ever ship.',
    benefits: [
      'Every current & future script',
      'Private server leaks & tools',
      'Reselling methods & guides',
      'Early access to new scripts',
      'Premium VIP Discord roles',
      'Concierge priority support',
    ],
    featured: true,
    badge: 'All access · Lifetime',
  },
];

function computeSalePrice(basePrice, sale, kind) {
  if (!sale?.active) return null;
  const applies = sale.applies_to || 'all';
  if (applies !== 'all' && applies !== kind) return null;
  if (sale.type === 'fixed') return Math.max(1, basePrice - Number(sale.value || 0));
  if (sale.type === 'percent') return Math.max(1, Math.round(basePrice * (1 - Number(sale.value || 0) / 100)));
  return null;
}

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [sale, setSale] = useState(null);

  useEffect(() => {
    api.get('/products').then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get('/sale/current').then((r) => setSale(r.data)).catch(() => setSale({ active: false }));
  }, []);

  const matrix = products.filter((p) => (p.category || 'matrix') === 'matrix');
  const zen = products.filter((p) => p.category === 'zen');

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-grain relative min-h-screen text-white overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative pt-32 pb-24 md:pt-40 md:pb-32 px-6"
      >
        {/* ambient orbs */}
        <div className="orb" style={{ top: '10%', left: '15%', width: 320, height: 320, background: 'radial-gradient(circle, rgb(var(--brand-rgb)/0.55), transparent 70%)' }} />
        <div className="orb" style={{ top: '30%', right: '10%', width: 260, height: 260, background: 'radial-gradient(circle, rgba(125,211,252,0.35), transparent 70%)' }} />

        <div className="mx-auto max-w-5xl text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70 font-mono uppercase tracking-widest mb-8"
            data-testid="hero-pill"
          >
            <Sparkles className="h-3 w-3 text-brand" />
            Premium XIM Matrix &amp; Cronus Zen Scripts
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-display font-black tracking-tighter text-white text-6xl sm:text-7xl md:text-8xl lg:text-[132px] leading-[0.9]"
            data-testid="hero-title"
          >
            <span className="lucky-glow text-brand">Lucky</span>{' '}
            <span className="text-white">Scripts</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-8 text-white/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-subtitle"
          >
            Premium XIM Matrix &amp; Cronus Zen scripts with instant digital delivery.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-10 flex items-center justify-center gap-4 flex-wrap"
          >
            <button
              data-testid="hero-browse-btn"
              onClick={() => scrollTo('products')}
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-brand text-[#0a0a0a] font-semibold hover:bg-brand/90 hover:shadow-[0_0_36px_rgb(var(--brand-rgb)/0.65)] transition-shadow"
            >
              Browse Products
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              data-testid="hero-vip-btn"
              onClick={() => scrollTo('vip')}
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
            >
              <Crown className="h-4 w-4 text-brand" />
              Become VIP
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-16 flex items-center justify-center gap-8 text-xs uppercase tracking-widest text-white/40 font-mono"
          >
            <span className="inline-flex items-center gap-2"><Zap className="h-3 w-3 text-brand" /> Instant Delivery</span>
            <span className="hidden sm:inline-flex items-center gap-2"><ShieldCheck className="h-3 w-3 text-brand" /> Secure Auth Downloads</span>
            <span className="inline-flex items-center gap-2"><InfinityIcon className="h-3 w-3 text-brand" /> Lifetime Updates (VIP)</span>
          </motion.div>
        </div>
      </section>

      {/* PRODUCTS – Matrix */}
      <section id="products" className="relative px-6 py-20 md:py-28" data-testid="products-section">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-xl">
              <p className="text-brand uppercase tracking-[0.35em] text-[11px] font-mono mb-4 inline-flex items-center gap-2">
                <span className="h-px w-6 bg-brand/60" />
                The Catalog
              </p>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter leading-[1.05]">
                Battle-tested scripts.<br/><span className="text-white/55">Delivered instantly.</span>
              </h2>
              <p className="mt-4 text-white/55 text-base">
                Every title is calibrated against the latest patch, shipped with a full install walkthrough, and backed by free lifetime updates.
              </p>
            </div>
            <div className="hidden md:flex flex-col items-end gap-1 text-right shrink-0">
              <span className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Starting at</span>
              <div className="flex items-baseline gap-2">
                {sale?.active && (sale.applies_to === 'all' || sale.applies_to === 'product') ? (
                  <>
                    <span className="font-display text-4xl font-black text-white">
                      ${Math.max(1, Math.round(30 * (1 - Number(sale.value || 0) / 100)))}
                    </span>
                    <span className="text-white/60 line-through text-lg">$30</span>
                    <span className="text-white/50 text-sm">USD</span>
                  </>
                ) : (
                  <>
                    <span className="font-display text-4xl font-black text-white">$30</span>
                    <span className="text-white/50 text-sm">USD</span>
                  </>
                )}
              </div>
              <span className="text-[11px] uppercase tracking-widest text-white/35 font-mono">per script · lifetime updates</span>
            </div>
          </div>

          {matrix.length === 0 ? (
            <EmptyCatalogState onVip={() => scrollTo('vip')} />
          ) : (
            <ProductGrid items={matrix} testid="products-grid" sale={sale} />
          )}
        </div>
      </section>

      {/* PRODUCTS – Zen */}
      {zen.length > 0 && (
        <section id="zen-scripts" className="relative px-6 py-16 md:py-24" data-testid="zen-products-section">
          <div className="mx-auto max-w-7xl">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-brand uppercase tracking-[0.35em] text-xs font-mono mb-3">Cronus Zen Scripts</p>
                <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">Rust <span className="text-brand">Zen</span> scripts.</h2>
                <p className="mt-3 text-white/60 text-base max-w-xl">Battle-tested Cronus Zen profiles for Rust Console — available individually.</p>
              </div>
              <div className="hidden md:block text-right">
                <div className="text-white/50 text-sm">Each script</div>
                <div className="font-display text-3xl font-bold">$20 <span className="text-white/50 text-base">USD</span></div>
              </div>
            </div>
            <ProductGrid items={zen} testid="zen-products-grid" />
          </div>
        </section>
      )}

      {/* VIP – single Premium tier · clean typographic hero (no box art) */}
      <section id="vip" className="relative px-6 py-24 md:py-32" data-testid="vip-section">
        <div className="mx-auto max-w-6xl">
          {VIP_TIERS.map((tier) => {
            const salePrice = computeSalePrice(tier.price, sale, 'vip');
            return (
              <motion.div
                key={tier.id}
                data-testid={`vip-tier-${tier.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-3xl overflow-hidden neon-frame"
                style={{
                  background:
                    'radial-gradient(ellipse at top, rgb(var(--brand-rgb)/0.18), transparent 55%),' +
                    'linear-gradient(180deg, #0a1324 0%, #060a16 100%)',
                  boxShadow: `0 40px 100px -30px ${tier.accentSoft}, inset 0 1px 0 rgba(255,255,255,0.04)`,
                }}
              >
                {/* Grid overlay for the "legit" cyber feel */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.07]"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgb(var(--brand-rgb)/0.4) 1px, transparent 1px),' +
                      'linear-gradient(90deg, rgb(var(--brand-rgb)/0.4) 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                    maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
                  }}
                />

                {salePrice && (
                  <div className="absolute top-5 right-5 z-10 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest font-mono font-semibold bg-emerald-400/15 border border-emerald-400/40 text-emerald-300">
                    SAVE ${tier.price - salePrice}
                  </div>
                )}

                <div className="relative px-6 md:px-14 py-14 md:py-20 text-center">
                  <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.4em] font-mono mb-6" style={{ color: tier.accent }}>
                    <span className="h-px w-10 bg-current" />
                    All Access · Lifetime
                    <span className="h-px w-10 bg-current" />
                  </p>

                  <h3
                    className="font-display font-black text-6xl md:text-8xl tracking-tighter mb-1 leading-[0.95]"
                    style={{
                      background: 'linear-gradient(180deg, #ffffff 10%, #9FC9FF 95%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textShadow: '0 0 60px rgb(var(--brand-rgb)/0.25)',
                    }}
                  >
                    PREMIUM VIP
                  </h3>
                  <p className="mt-5 text-white/55 text-base md:text-lg max-w-md mx-auto">{tier.tagline}</p>

                  <div className="mt-10 flex items-baseline justify-center gap-3 flex-wrap">
                    {salePrice ? (
                      <>
                        <span className="font-display font-black text-white text-7xl md:text-8xl tracking-tighter">${salePrice}</span>
                        <span className="text-white/50 text-base">USD</span>
                        <span className="text-white/35 text-2xl line-through tracking-tighter ml-2">${tier.price}</span>
                      </>
                    ) : (
                      <>
                        <span className="font-display font-black text-white text-7xl md:text-8xl tracking-tighter">${tier.price}</span>
                        <span className="text-white/50 text-base">USD</span>
                      </>
                    )}
                  </div>
                  <p className="text-white/40 text-xs mt-2 font-mono uppercase tracking-widest">one-time · instant access</p>

                  <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-3 max-w-2xl mx-auto text-left">
                    {tier.benefits.map((b) => (
                      <div key={b} className="flex items-start gap-3 text-[15px] text-white/80">
                        <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full shrink-0" style={{ background: "rgb(var(--brand-rgb) / 0.13)", color: tier.accent }}>
                          <Check className="h-3 w-3" />
                        </span>
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={`/checkout/vip?tier=${tier.id}`}
                    data-testid={`vip-cta-${tier.id}`}
                    className="mt-12 inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-semibold text-lg text-[#050812] transition-shadow"
                    style={{ background: tier.accent }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 60px ${tier.accentSoft}`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 0 0 rgba(0,0,0,0)'; }}
                  >
                    <Crown className="h-5 w-5" /> Become Premium VIP
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <ReviewsMarquee />

      {/* Discord CTA */}
      <section className="relative px-6 py-16" data-testid="discord-cta-section">
        <div className="mx-auto max-w-5xl">
          <div
            className="group relative block overflow-hidden rounded-3xl border border-[#5865F2]/30 bg-[#5865F2]/[0.08] p-10 md:p-14 hover:border-[#5865F2]/60 transition-colors"
          >
            <div
              className="orb"
              style={{ top: '-40%', right: '-10%', width: 500, height: 500,
                       background: 'radial-gradient(circle, rgba(88,101,242,0.5), transparent 70%)' }}
            />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/40 bg-[#5865F2]/15 px-3 py-1 text-xs text-[#B5B9F5] font-mono uppercase tracking-widest mb-5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19.5 4.5c-1.5-.7-3.1-1.2-4.8-1.5-.2.4-.5 1-.7 1.4-1.8-.3-3.6-.3-5.3 0-.2-.4-.5-1-.7-1.4C6.3 3.3 4.7 3.8 3.2 4.5 0 9.3-.8 14 .1 18.6c2 1.5 3.9 2.4 5.8 3 .5-.6.9-1.3 1.3-2-.7-.3-1.4-.6-2-1 .2-.1.3-.2.5-.3 3.9 1.8 8.1 1.8 12 0 .2.1.3.2.5.3-.6.4-1.3.7-2 1 .4.7.8 1.4 1.3 2 1.9-.6 3.8-1.5 5.8-3 1-5.3-.3-9.9-3.8-14.1zM8.5 15.9c-1.1 0-2-1-2-2.3s.9-2.3 2-2.3 2 1 2 2.3-.9 2.3-2 2.3zm7 0c-1.1 0-2-1-2-2.3s.9-2.3 2-2.3 2 1 2 2.3-.9 2.3-2 2.3z"/></svg>
                  Join the Community
                </div>
                <h3 className="font-display font-black text-white text-4xl md:text-5xl tracking-tighter leading-none">
                  Live in the <span className="text-[#7B85F5]">Discord</span>.
                </h3>
                <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
                  Instant support, weekly patch notes, VIP-only channels and behind-the-scenes drops. Every buyer gets access — jump in.
                </p>
                <div className="mt-6 flex items-center gap-6 text-xs uppercase tracking-widest text-white/40 font-mono">
                  <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> 2,400+ members</span>
                  <span className="hidden sm:inline-flex items-center gap-2">Priority Support</span>
                </div>
              </div>
              <div className="flex flex-col items-stretch gap-3 md:min-w-[220px]">
                <a
                  href="https://discord.gg/luckyscripts"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="discord-join-btn"
                  className="inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#5865F2] text-white font-semibold text-lg hover:shadow-[0_0_44px_rgba(88,101,242,0.7)] transition-shadow"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.5 4.5c-1.5-.7-3.1-1.2-4.8-1.5-.2.4-.5 1-.7 1.4-1.8-.3-3.6-.3-5.3 0-.2-.4-.5-1-.7-1.4C6.3 3.3 4.7 3.8 3.2 4.5 0 9.3-.8 14 .1 18.6c2 1.5 3.9 2.4 5.8 3 .5-.6.9-1.3 1.3-2-.7-.3-1.4-.6-2-1 .2-.1.3-.2.5-.3 3.9 1.8 8.1 1.8 12 0 .2.1.3.2.5.3-.6.4-1.3.7-2 1 .4.7.8 1.4 1.3 2 1.9-.6 3.8-1.5 5.8-3 1-5.3-.3-9.9-3.8-14.1zM8.5 15.9c-1.1 0-2-1-2-2.3s.9-2.3 2-2.3 2 1 2 2.3-.9 2.3-2 2.3zm7 0c-1.1 0-2-1-2-2.3s.9-2.3 2-2.3 2 1 2 2.3-.9 2.3-2 2.3z"/></svg>
                  Join Discord
                </a>
                <DiscordCopyButton />
                <p className="text-[10px] text-center text-white/40 leading-relaxed max-w-[220px]">
                  Discord blocked here? Copy the invite &amp; open on your phone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
